import React, { useState, useMemo } from 'react';
import { supabase } from '../supabase';
import CustomerModal from './CustomerModal';
import CustomerDetailModal from './CustomerDetailModal';
import ExportButtons from './ExportButtons';
import ImportModal from './ImportModal';

function typeBadge(type) {
  const map = { Architect: 'badge-architect', Contractor: 'badge-contractor', Individual: 'badge-individual', 'Planning Consultant': 'badge-planning' };
  return <span className={`badge ${map[type] || 'badge-individual'}`}>{type}</span>;
}

function SortTh({ label, sortKey, sortState, onSort }) {
  const [key, dir] = sortState;
  const active = key === sortKey;
  return (
    <th style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }} onClick={() => onSort(sortKey)}>
      {label} <span style={{ color: active ? 'var(--accent)' : 'var(--text3)', fontSize: 10 }}>{active ? (dir === 'asc' ? '▲' : '▼') : '⇅'}</span>
    </th>
  );
}

export default function CustomersPage({ customers, projects, onRefresh, user }) {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [modal, setModal] = useState(null);
  const [detailModal, setDetailModal] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [starredOnly, setStarredOnly] = useState(false);

  const toggleStar = async (e, c) => {
    e.stopPropagation();
    await supabase.from('customers').update({ starred: !c.starred }).eq('id', c.id);
    onRefresh();
  };
  const [importing, setImporting] = useState(false);
  const [sort, setSort] = useState(['full_name', 'asc']);

  const handleSort = (key) => {
    setSort(([k, d]) => k === key ? [key, d === 'asc' ? 'desc' : 'asc'] : [key, 'asc']);
  };

  const projectCount = (id) => projects.filter(p => p.customer_id === id && p.status !== 'Rejected').length;
  const agreedFees = (id) => {
    const total = projects
      .filter(p => p.customer_id === id && p.status !== 'Rejected')
      .reduce((s, p) => s + (p.financials?.[0]?.agreed_fee || 0), 0);
    return total > 0 ? `£${Number(total).toLocaleString('en-GB', { minimumFractionDigits: 0 })}` : '—';
  };

  const filtered = useMemo(() => {
    let list = customers.filter(c => {
      const q = search.toLowerCase();
      const match = `${c.full_name} ${c.company} ${c.email}`.toLowerCase().includes(q);
      return match && (!typeFilter || c.type === typeFilter) && (!starredOnly || c.starred);
    });

    const [key, dir] = sort;
    list.sort((a, b) => {
      let av, bv;
      if (key === 'projects') { av = projectCount(a.id); bv = projectCount(b.id); }
      else if (key === 'starred') { av = a.starred ? 1 : 0; bv = b.starred ? 1 : 0; }
      else if (key === 'agreed') {
        const getTotal = id => projects.filter(p => p.customer_id === id && p.status !== 'Rejected').reduce((s, p) => s + (p.financials?.[0]?.agreed_fee || 0), 0);
        av = getTotal(a.id); bv = getTotal(b.id);
      }
      else { av = (a[key] || '').toLowerCase(); bv = (b[key] || '').toLowerCase(); }
      if (av < bv) return dir === 'asc' ? -1 : 1;
      if (av > bv) return dir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [customers, projects, search, typeFilter, sort]);

  const deleteCustomer = async (id) => {
    if (!window.confirm('Delete this customer? Their projects will remain but unlinked.')) return;
    setDeleting(id);
    await supabase.from('customers').delete().eq('id', id);
    onRefresh();
    setDeleting(null);
  };

  return (
    <div style={{ padding: '28px 32px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, marginBottom: 4 }}>Customers</h1>
          <p style={{ color: 'var(--text3)', fontSize: 13 }}>{customers.length} total</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <ExportButtons
            rows={filtered}
            columns={[
              { label: 'Code', get: c => c.customer_code },
              { label: 'Name', get: c => c.full_name },
              { label: 'Company', get: c => c.company },
              { label: 'Type', get: c => c.type },
              { label: 'Email', get: c => c.email },
              { label: 'Phone', get: c => c.phone },
              { label: 'Notes', get: c => c.notes },
              { label: 'Added By', get: c => c.created_by_name },
            ]}
            title="Customers"
            filename="liongate_customers"
          />
          <button className="btn-primary" onClick={() => setModal('new')}>+ New Customer</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
        <div className="search-bar" style={{ flex: 1 }}>
          <span className="icon">⌕</span>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, company, email..." />
        </div>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} style={{ width: 200 }}>
          <option value="">All types</option>
          <option value="Architect">Architect / Designer</option>
          <option value="Contractor">Contractor</option>
          <option value="Individual">Individual</option>
          <option value="Planning Consultant">Planning Consultant</option>
        </select>
        <button
          onClick={() => setStarredOnly(v => !v)}
          style={{ padding: '9px 16px', borderRadius: 'var(--radius)', border: '1px solid var(--border2)', background: starredOnly ? '#fbbf24' : 'transparent', color: starredOnly ? '#0d0f12' : 'var(--text2)', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}
        >⭐ {starredOnly ? 'Starred' : 'All'}</button>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'auto' }}>
        {filtered.length === 0 ? (
          <div className="empty-state">
            <div className="icon">👥</div>
            <p>No customers found</p>
            <span>Try adjusting your search or add a new customer</span>
          </div>
        ) : (
          <table className="table compact-register customers-table">
            <thead>
              <tr>
                <th style={{ width: 36 }}></th>
                
                <SortTh label="Name" sortKey="full_name" sortState={sort} onSort={handleSort} />
                <SortTh label="Company" sortKey="company" sortState={sort} onSort={handleSort} />
                <SortTh label="Type" sortKey="type" sortState={sort} onSort={handleSort} />
                <th>Email</th>
                
                
                
                <SortTh label="Projects" sortKey="projects" sortState={sort} onSort={handleSort} />
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => (
                <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => setDetailModal(c)}>
                  <td onClick={e => toggleStar(e, c)} style={{ cursor: 'pointer', textAlign: 'center', fontSize: 16 }}>
                    {c.starred ? '⭐' : <span style={{ color: 'var(--border2)', fontSize: 14 }}>☆</span>}
                  </td>
                  
                  <td className="td-main">{c.full_name}</td>
                  <td>{c.company || '—'}</td>
                  <td>{typeBadge(c.type)}</td>
                  <td>{c.email ? <a href={`mailto:${c.email}`} style={{ color: 'var(--blue)', textDecoration: 'none' }} onClick={e => e.stopPropagation()}>{c.email}</a> : '—'}</td>
                  
                  
                  
                  <td><span style={{ color: 'var(--text)', fontWeight: 500 }}>{projectCount(c.id)}</span></td>
                  <td onClick={e => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      <button className="btn-ghost" style={{ padding: '5px 10px', fontSize: 12 }} onClick={() => setModal(c)}>Edit</button>
                      <button className="btn-danger" style={{ padding: '5px 10px', fontSize: 12 }} onClick={() => deleteCustomer(c.id)} disabled={deleting === c.id}>Del</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {importing && <ImportModal type="customers" onClose={() => setImporting(false)} onDone={() => { setImporting(false); onRefresh(); }} />}
      {detailModal && (
        <CustomerDetailModal customer={detailModal} projects={projects} onClose={() => setDetailModal(null)} onRefresh={() => { setDetailModal(null); onRefresh(); }} user={user} />
      )}
      {modal && (
        <CustomerModal customer={modal === 'new' ? null : modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); onRefresh(); }} user={user} />
      )}
    </div>
  );
}
